/**
 * Live / finished match stats — possession bar + comparison rows; surfaces follow light/dark theme.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { pickContrastTextOnBackground } from '../../../helpers/pickContrastText.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import type { MatchHubMatchStats, MatchHubTeamSide } from '../../../src/match/types/matchHub.types';
import { getMatchHubLiveStatsBlockStyles } from './MatchHubLiveStatsBlock.styles';

export interface MatchHubLiveStatsBlockProps {
  home: MatchHubTeamSide;
  away: MatchHubTeamSide;
  stats: MatchHubMatchStats;
}

type StatRowDef = { key: string; label: string; home: number; away: number };

export const MatchHubLiveStatsBlock: React.FC<MatchHubLiveStatsBlockProps> = ({
  home,
  away,
  stats,
}) => {
  const theme = useTheme();
  const { isDark } = useThemeContext();
  const styles = useMemo(() => getMatchHubLiveStatsBlockStyles(theme, isDark), [theme, isDark]);

  const possessionHome = Math.min(100, Math.max(0, Math.round(stats.possessionHome)));
  const possessionAway = 100 - possessionHome;

  const awayBarTextColor = pickContrastTextOnBackground(away.kit.primary);

  const rows = useMemo<StatRowDef[]>(
    () => [
      { key: 'shots', label: 'Shots', home: stats.shotsHome, away: stats.shotsAway },
      {
        key: 'sot',
        label: 'Shots on target',
        home: stats.shotsOnTargetHome,
        away: stats.shotsOnTargetAway,
      },
      { key: 'corners', label: 'Corners', home: stats.cornersHome, away: stats.cornersAway },
      { key: 'fouls', label: 'Fouls', home: stats.foulsHome, away: stats.foulsAway },
      {
        key: 'yellow',
        label: 'Yellow cards',
        home: stats.yellowHome,
        away: stats.yellowAway,
      },
      { key: 'red', label: 'Red cards', home: stats.redHome, away: stats.redAway },
    ],
    [stats],
  );

  return (
    <View style={styles.wrap} testID="match-hub-live-stats">
      <View style={styles.possessionTrack}>
        <View style={[styles.possessionSegBase, styles.possessionHomeSeg, { flex: Math.max(1, possessionHome) }]}>
          <Text style={styles.possessionPctHome}>{possessionHome}%</Text>
        </View>
        <View
          style={[
            styles.possessionSegBase,
            styles.possessionSegAway,
            { flex: Math.max(1, possessionAway), backgroundColor: away.kit.primary },
          ]}
        >
          <Text style={[styles.possessionPctAway, { color: awayBarTextColor }]}>{possessionAway}%</Text>
        </View>
      </View>

      {rows.map((row, index) => (
        <View
          key={row.key}
          style={[styles.statRow, index === 0 && styles.statRowFirst]}
          accessibilityLabel={`${row.label}: ${home.shortName} ${row.home}, ${away.shortName} ${row.away}`}
        >
          <View style={styles.homePill}>
            <Text style={styles.homePillText}>{row.home}</Text>
          </View>
          <Text style={styles.statLabel} numberOfLines={2}>
            {row.label}
          </Text>
          <Text style={styles.awayValue}>{row.away}</Text>
        </View>
      ))}
    </View>
  );
};
